/* eslint-disable max-lines */
/* eslint-disable max-len */
/* eslint-disable react/no-unstable-nested-components */
import { FaIcon, GraphLegend, NonIdealState } from '@acp/core-ui';
import {
    Box, VStack, useBreakpointValue, useToken,
} from '@chakra-ui/react';
import {
    BarCustomLayerProps, BarDatum, ComputedDatum, ResponsiveBar,
} from '@nivo/bar';
import { BasicTooltip } from '@nivo/tooltip';
import { IntegratedPortfolioFacets, PortfolioFacets } from '@acp/shared';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { useGetNonManagedStats } from '../../../hooks/use-get-stats';
import { BarChartSkeleton } from '../graph-skeleton';

// Lets us customise the display value of bar totals,
// by reimplementing bar totals instead of using Nivo's built-in one.
// Adapted from GH issue:
// https://github.com/plouc/nivo/issues/2020#issuecomment-1140295641
function BarTotalsLayer({
    bars,
    yScale,
    xScale,
}: BarCustomLayerProps<BarDatum>) {
    const labelOffset = 10;
    const labelFontSize = 12;
    if (bars.length === 0) return null;

    // compute totals for each index/bar
    const totals: Record<string | number, {
        actualTotal: number;
        value: number;
    }> = {};

    const bandwidth = bars[0].width;

    bars.forEach((bar) => {
        const { indexValue } = bar.data;

        if (!(indexValue in totals)) {
            totals[indexValue] = {
                actualTotal: 0,
                value: 0,
            };
        }

        // Can use totals[].actualValue to tally the actual total for each bar
        // and use totals[].value to know how high to place the label
        if (!bar.data.hidden && bar.data.value !== null && bar.data.data[`${bar.data.id}Actual`] !== undefined) {
            totals[indexValue].actualTotal += bar.data.data[`${bar.data.id}Actual`] as number;
            totals[indexValue].value += bar.data.value!;
        }
    });

    // place text elements above the bars
    const labels = Object.keys(totals).map((indexValue) => {
        const x = xScale(indexValue as unknown as number) + bandwidth / 2;
        const y = yScale(totals[indexValue].value) - labelOffset;

        return (
            <text
                key={`total.${indexValue}`}
                x={x}
                y={y}
                textAnchor="middle"
                fontWeight="bold"
                fontSize={labelFontSize}
            >
                {totals[indexValue].actualTotal}
            </text>
        );
    });

    return labels;
}

export function ComplianceSummaryGraph() {
    const missingDataBarColor = useToken('colors', 'secondary.200');
    const overdueBarColor = useToken('colors', 'secondary.100');
    const approachingNextServiceBarColor = useToken('colors', 'secondary.50');

    const fontSizeSm = useToken('fontSizes', 'sm');
    const barMargin = useBreakpointValue({
        base: {
            top: 50, right: 30, bottom: 70, left: 0,
        },
        sm: {
            top: 50, right: 30, bottom: 60, left: 40,
        },
    });

    const navigate = useNavigate();

    const { isLoading, data } = useGetNonManagedStats();

    const graphData = useMemo(() => {
        const barDatum: unknown[] = [];

        if (data === undefined) {
            return barDatum;
        }

        const {
            OVERDUE, APPROACHING_NEXT_SERVICE, MISSING_SUPPLIER, MISSING_LAST_SERVICE_DATE,
            FURTHER_WORKS_REQUIRED,
        } = data.nonManagedPortfolioStats;

        const totalUpcomingServices = OVERDUE + APPROACHING_NEXT_SERVICE;

        if (OVERDUE > 0 && APPROACHING_NEXT_SERVICE > 0) {
            barDatum.push({
                labelName: 'Upcoming services',
                type: 'Upcoming services',
                OverdueActual: OVERDUE,
                Overdue: (100 / totalUpcomingServices) * OVERDUE,
                OverdueColor: overdueBarColor,
                OverdueFacet: [IntegratedPortfolioFacets.Overdue],
                'Approaching next serviceActual': APPROACHING_NEXT_SERVICE,
                'Approaching next service': (100 / totalUpcomingServices) * APPROACHING_NEXT_SERVICE,
                'Approaching next serviceColor': approachingNextServiceBarColor,
                'Approaching next serviceFacet': [IntegratedPortfolioFacets.ApproachingNextService],
            });
        } else if (OVERDUE > 0) {
            barDatum.push({
                labelName: 'Upcoming services',
                type: 'Upcoming services',
                OverdueActual: OVERDUE,
                Overdue: (100 / totalUpcomingServices) * OVERDUE,
                OverdueColor: overdueBarColor,
                OverdueFacet: [IntegratedPortfolioFacets.Overdue],
            });
        } else if (APPROACHING_NEXT_SERVICE > 0) {
            barDatum.push({
                labelName: 'Upcoming services',
                type: 'Upcoming services',
                'Approaching next serviceActual': APPROACHING_NEXT_SERVICE,
                'Approaching next service': (100 / totalUpcomingServices) * APPROACHING_NEXT_SERVICE,
                'Approaching next serviceColor': approachingNextServiceBarColor,
                'Approaching next serviceFacet': [IntegratedPortfolioFacets.ApproachingNextService],
            });
        }

        if (MISSING_SUPPLIER > 0) {
            barDatum.push({
                labelName: 'Missing service provider',
                type: 'Missing service provider',
                'Missing service providerActual': MISSING_SUPPLIER,
                'Missing service provider': 100,
                'Missing service providerColor': missingDataBarColor,
                'Missing service providerFacet': [IntegratedPortfolioFacets.MissingSupplier],
            });
        }

        if (MISSING_LAST_SERVICE_DATE > 0) {
            barDatum.push({
                labelName: 'Missing last service date',
                type: 'Missing last service date',
                'Missing last service dateActual': MISSING_LAST_SERVICE_DATE,
                'Missing last service date': 100,
                'Missing last service dateColor': missingDataBarColor,
                'Missing last service dateFacet': [IntegratedPortfolioFacets.MissingLastServiceDate],
            });
        }

        if (FURTHER_WORKS_REQUIRED > 0) {
            barDatum.push({
                labelName: 'Rectification required',
                type: 'Rectification required',
                'Rectification requiredActual': FURTHER_WORKS_REQUIRED,
                'Rectification required': 100,
                'Rectification requiredColor': missingDataBarColor,
                'Rectification requiredFacet': [IntegratedPortfolioFacets.FurtherWorksRequired],
            });
        }

        return barDatum;
    }, [data]);

    const keys = useMemo(() => {
        const resultKeys: string[] = [];

        if (data === undefined) {
            return resultKeys;
        }

        const {
            OVERDUE, APPROACHING_NEXT_SERVICE, MISSING_SUPPLIER, MISSING_LAST_SERVICE_DATE, FURTHER_WORKS_REQUIRED,
        } = data.nonManagedPortfolioStats;

        if (APPROACHING_NEXT_SERVICE > 0) resultKeys.push('Approaching next service');
        if (OVERDUE > 0) resultKeys.push('Overdue');
        if (MISSING_SUPPLIER > 0) resultKeys.push('Missing service provider');
        if (MISSING_LAST_SERVICE_DATE > 0) resultKeys.push('Missing last service date');
        if (FURTHER_WORKS_REQUIRED > 0) resultKeys.push('Rectification required');
        return resultKeys;
    }, [data]);

    if (isLoading || data === undefined) {
        return <BarChartSkeleton />;
    }

    const handleClickBar = (datum: ComputedDatum<BarDatum>) => {
        const facet = datum.data[`${datum.id}Facet`] as unknown as PortfolioFacets[] | undefined;

        if (facet !== undefined) {
            const searchParams = new URLSearchParams();

            facet.forEach((f) => {
                searchParams.append('facet', f);
            });

            navigate(`/portfolio?managed-by=OTHER&${searchParams.toString()}`, {
                replace: true,
            });
        }
    };

    if (graphData.length === 0) {
        return (
            <NonIdealState
                iconComponent={<FaIcon icon={faExclamationCircle} size="9x" color="muted.200" />}
                title="There are no properties on service with other providers. If this is an error please contact your account manager"
                py={24}
            />
        );
    }

    const getLegendData = (
        labels: string[],
        barData: BarDatum[]
    ) => labels.map((label) => {
        const datum = barData.find((bd) => bd[`${label}Color`] !== undefined);
        if (datum) {
            return {
                label,
                legendIconColor: datum[`${label}Color`] as string,
            };
        }
        return {
            label,
            legendIconColor: null,
        };
    });

    return (
        <VStack w="full">
            <Box w="99%" h={96} className="svg-overflow-visible">
                <ResponsiveBar
                    data={graphData as unknown as BarDatum[]}
                    keys={keys}
                    indexBy="type"
                    margin={barMargin}
                    padding={0.3}
                    valueScale={{ type: 'linear' }}
                    indexScale={{ type: 'band', round: true }}
                    axisLeft={null}
                    colors={({ data: colorData, id }) => {
                        if (id === 'Missing service provider' || id === 'Missing last service date') {
                            return missingDataBarColor;
                        }
                        return colorData[`${id}Color`] as string;
                    }}
                    tooltip={({
                        color, formattedValue, indexValue, id, data: tooltipData,
                    }) => (
                        <BasicTooltip
                            id={id === indexValue ? tooltipData.labelName : id}
                            value={tooltipData[`${id}Actual`] !== undefined ? tooltipData[`${id}Actual`] : formattedValue}
                            enableChip
                            color={color}
                        />
                    )}
                    enableGridY={false}
                    labelSkipWidth={12}
                    labelSkipHeight={25}
                    labelTextColor="black"
                    layers={[
                        'grid',
                        'axes',
                        'markers',
                        'bars',
                        'legends',
                        'annotations',
                        BarTotalsLayer,

                    ]}
                    axisBottom={{
                        renderTick: (tick) => (
                            <g transform={`translate(${tick.x},${tick.y + 10})`}>
                                <foreignObject width={80} height={40} x={-40} y={0}>
                                    <div
                                        style={{
                                            fontSize: 12,
                                            lineHeight: 1.1,
                                            textAlign: 'center',
                                            wordWrap: 'break-word',
                                            whiteSpace: 'normal',
                                        }}
                                    >
                                        {tick.value}
                                    </div>
                                </foreignObject>
                            </g>
                        ),
                    }}
                    enableLabel
                    label={(d) => `${d.data[`${d.id}Actual`]}`}
                    theme={{
                        text: {
                            fontFamily: "'Roboto'",
                        },
                        labels: {
                            text: {
                                fontSize: fontSizeSm,
                            },
                        },
                        tooltip: {
                            container: {
                                fontSize: fontSizeSm,
                            },
                            chip: {
                                borderRadius: '100px',
                            },
                        },
                        axis: {
                            ticks: {
                                text: {
                                    width: 10,
                                },
                            },
                            legend: {
                                text: {
                                    color: 'blue',
                                },
                            },
                        },
                    }}
                    onClick={handleClickBar}
                    onMouseEnter={(_datum, event) => {
                        // Not great, works perfectly
                        // https://github.com/plouc/nivo/issues/2276#issuecomment-1496478882
                        // eslint-disable-next-line no-param-reassign
                        event.currentTarget.style.cursor = 'pointer';
                    }}
                />
            </Box>
            <GraphLegend data={getLegendData(keys, graphData as BarDatum[])} />
        </VStack>
    );
}
